import { drawProjectionBorder } from '../renderer/renderer.js';

/**
 * Layout calculation utilities for optimal sizing of projection and globe
 * Uses constraint-based optimization to maximize space usage while respecting:
 * - Projection aspect ratios
 * - Proportional globe sizing (1/3 of projection width)
 * - Vertical margins and spacing
 * - Viewport boundaries (no overflow)
 */

/**
 * Calculate optimal sizes for projection and globe that maximize space usage
 * while respecting all layout constraints
 *
 * @param {number} viewportWidth - Available viewport width
 * @param {number} viewportHeight - Available viewport height
 * @param {number} aspectRatio - Current projection aspect ratio (height/width)
 * @param {Object} layout - Layout constants (margins, padding, etc.)
 * @returns {Object} { projectionWidth, projectionHeight, globeSize, canFit }
 */
export function calculateOptimalSizes(viewportWidth, viewportHeight, aspectRatio, layout) {
    const {
        PROJECTION_TOP_MARGIN,
        GLOBE_MARGIN,
        GLOBE_BOTTOM_PADDING,
        MIN_PROJECTION_WIDTH,
        MIN_GLOBE_SIZE,
        PROJECTION_GLOBE_SIZE_RATIO
    } = layout;

    // Calculate available vertical space
    const availableVertical = viewportHeight - PROJECTION_TOP_MARGIN - GLOBE_MARGIN - GLOBE_BOTTOM_PADDING;
    const availableHorizontal = viewportWidth;

    // Binary search for optimal projection width
    let minWidth = MIN_PROJECTION_WIDTH;
    let maxWidth = availableHorizontal;
    let bestSolution = null;

    // Iteratively find maximum size that fits all constraints
    for (let attempt = 0; attempt < 20; attempt++) {
        const testProjectionWidth = (minWidth + maxWidth) / 2;
        const testProjectionHeight = testProjectionWidth * aspectRatio;
        const testGlobeSize = Math.max(MIN_GLOBE_SIZE, testProjectionWidth * PROJECTION_GLOBE_SIZE_RATIO);

        const totalVertical = testProjectionHeight + testGlobeSize;
        const maxHorizontal = Math.max(testProjectionWidth, testGlobeSize);

        const fitsVertically = totalVertical <= availableVertical;
        const fitsHorizontally = maxHorizontal <= availableHorizontal;

        if (fitsVertically && fitsHorizontally) {
            bestSolution = {
                projectionWidth: Math.floor(testProjectionWidth),
                projectionHeight: Math.floor(testProjectionHeight),
                globeSize: Math.floor(testGlobeSize),
                canFit: true
            };
            minWidth = testProjectionWidth; // Try bigger
        } else {
            maxWidth = testProjectionWidth; // Try smaller
        }
    }

    // If no solution found, use minimum sizes
    if (!bestSolution) {
        const projWidth = Math.min(MIN_PROJECTION_WIDTH, availableHorizontal);
        return {
            projectionWidth: projWidth,
            projectionHeight: Math.floor(projWidth * aspectRatio),
            globeSize: Math.min(MIN_GLOBE_SIZE, availableHorizontal),
            canFit: false
        };
    }

    return bestSolution;
}

/**
 * Calculate positioning for projection and globe
 * @param {Object} sizes - Sizes from calculateOptimalSizes
 * @param {number} viewportWidth - Viewport width
 * @param {Object} layout - Layout constants
 * @returns {Object} { projection: {x, y}, globe: {x, y} }
 */
export function calculatePositions(sizes, viewportWidth, layout) {
    const { projectionWidth, projectionHeight, globeSize } = sizes;
    const { PROJECTION_TOP_MARGIN, GLOBE_MARGIN } = layout;

    // Center projection horizontally
    const projectionX = Math.max(0, (viewportWidth - projectionWidth) / 2);
    const projectionY = PROJECTION_TOP_MARGIN;

    // Center globe horizontally, position below projection
    const globeX = Math.max(0, (viewportWidth - globeSize) / 2);
    const globeY = projectionY + projectionHeight + GLOBE_MARGIN;

    return {
        projection: { x: projectionX, y: projectionY },
        globe: { x: globeX, y: globeY }
    };
}

/**
 * Apply calculated sizes and positions to renderer
 * Consolidates layout update logic used by resize and projection change handlers
 *
 * @param {Object} app - Application state
 * @param {Object} sizes - Calculated sizes
 * @param {Object} positions - Calculated positions
 */
export function applyLayout(app, sizes, positions) {
    // Update projection container size and position
    app.renderer.projection.width = sizes.projectionWidth;
    app.renderer.projection.height = sizes.projectionHeight;
    app.renderer.projection.container.x = positions.projection.x;
    app.renderer.projection.container.y = positions.projection.y;

    // Update globe size and position
    app.renderer.globe.size = sizes.globeSize;
    app.renderer.globe.radius = sizes.globeSize / 2;
    app.renderer.globe.canvas.width = sizes.globeSize;
    app.renderer.globe.canvas.height = sizes.globeSize;
    app.renderer.globe.canvas.style.left = `${positions.globe.x}px`;
    app.renderer.globe.canvas.style.top = `${positions.globe.y}px`;
    app.renderer.globe.bounds.x = positions.globe.x;
    app.renderer.globe.bounds.y = positions.globe.y;
    app.renderer.globe.bounds.width = sizes.globeSize;
    app.renderer.globe.bounds.height = sizes.globeSize;

    // Update earth model canvas dimensions
    app.earthModel.setCanvasDimensions(sizes.projectionWidth, sizes.projectionHeight);

    // Redraw ocean background with new dimensions
    app.renderer.projection.oceanGraphics.clear();
    app.renderer.projection.oceanGraphics.beginFill(0x1a1a2e);
    app.renderer.projection.oceanGraphics.drawRect(0, 0, sizes.projectionWidth, sizes.projectionHeight);
    app.renderer.projection.oceanGraphics.endFill();

    // Update clipping mask
    if (app.renderer.projection.mask) {
        app.renderer.projection.mask.clear();
        app.renderer.projection.mask.beginFill(0xffffff);
        app.renderer.projection.mask.drawRect(0, 0, sizes.projectionWidth, sizes.projectionHeight);
        app.renderer.projection.mask.endFill();
    }

    // Update projection border
    if (app.renderer.projection.border) {
        app.renderer.projection.border.clear();
        drawProjectionBorder(app.renderer.projection.border, positions.projection, sizes.projectionWidth, sizes.projectionHeight);
    }
}
